import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * AR Customer Comms Notes RPC Contract
 *
 * A single, editable block of free-text guidance per customer describing how
 * the AR team wants to communicate with them (tone, who to CC, recurring
 * disputes to address up front, etc.). Distinct from the activity-timeline
 * notes (arCustomerComms.addCustomerNote) which are dated, append-only,
 * human-facing log entries — these comms notes are a curated steer that gets
 * injected into the LLM prompts (collection email drafts, short-pay
 * recommendations) so AI-generated comms improve over time.
 */

export const MAX_COMMS_NOTES_LENGTH = 4000;

export const ArCustomerCommsNotesSchema = z.object({
  /** The guidance text; null when none has been set. */
  notes: z.string().nullable(),
  /** ISO datetime of the last update; null if never set. */
  updatedAt: z.string().nullable(),
  /** User who last edited the notes; null if never set. */
  updatedByUserId: z.string().uuid().nullable(),
});

export type ArCustomerCommsNotes = z.infer<typeof ArCustomerCommsNotesSchema>;

export const GetArCustomerCommsNotesInputSchema = z.object({
  customerId: z.string().uuid(),
});

export const SetArCustomerCommsNotesInputSchema = z.object({
  customerId: z.string().uuid(),
  /** Pass an empty string to clear the notes. */
  notes: z.string().max(MAX_COMMS_NOTES_LENGTH),
});

export const AppendArCustomerCommsNotesInputSchema = z.object({
  customerId: z.string().uuid(),
  /** A single line of guidance to append to the running notes. */
  note: z.string().min(1).max(MAX_COMMS_NOTES_LENGTH),
});

export const arCustomerCommsNotesContracts = {
  /** Retrieve the comms-notes guidance for a customer. */
  get: oc
    .input(GetArCustomerCommsNotesInputSchema)
    .output(ArCustomerCommsNotesSchema)
    .route({
      method: "GET",
      summary: "Get customer communication guidance notes",
      description:
        "Returns a customer's free-text guidance on how to communicate with them (tone, CC contacts, recurring disputes), with last editor and update time.",
    }),
  /** Create, update, or clear the comms-notes guidance for a customer. */
  set: oc
    .input(SetArCustomerCommsNotesInputSchema)
    .output(ArCustomerCommsNotesSchema)
    .route({
      method: "POST",
      summary: "Set customer communication guidance notes",
      description:
        "Replaces a customer's communication guidance text; an empty string clears it. The notes steer AI-drafted collection emails and short-pay recommendations.",
    }),
  /** Append one line of guidance to the running comms notes (atomic). */
  append: oc
    .input(AppendArCustomerCommsNotesInputSchema)
    .output(ArCustomerCommsNotesSchema)
    .route({
      method: "POST",
      summary: "Add a line to customer communication notes",
      description:
        "Appends one line of guidance to a customer's communication notes atomically and returns the updated notes used to steer AI-drafted collection emails.",
    }),
} as const;

export type GetArCustomerCommsNotesInput = z.infer<
  typeof GetArCustomerCommsNotesInputSchema
>;
export type SetArCustomerCommsNotesInput = z.infer<
  typeof SetArCustomerCommsNotesInputSchema
>;
export type AppendArCustomerCommsNotesInput = z.infer<
  typeof AppendArCustomerCommsNotesInputSchema
>;
